import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const KyuStorePinterestEsport: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Pinterest-Inspired Clean Esport Palette (Official Kyu Store Tokens)
  const bgCanvas = "#F8F6FE";
  const bgCard = "#FFFFFF";
  const kyuPurple = "#5D3FD3";
  const kyuDark = "#1E124A";
  const kyuLavender = "#F3E8FF";
  const kyuBorder = "#E9D5FF";
  const accentGold = "#FF9F43";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldSuccess = "#10B981";
  const emeraldLight = "#ECFDF5";

  // Dynamic Subtitle Placeholder (Anchored in Safe Zone: bottom 220px)
  const SUBTITLES = [
    { from: 0, to: 90, text: "Top-up Mobile Legends termurah & tercepat se-Indonesia.", highlight: "termurah & tercepat" },
    { from: 90, to: 180, text: "Weekly Diamond Pass hemat 70%, resmi via ID game tanpa password.", highlight: "hemat 70%" },
    { from: 180, to: 270, text: "Scan QRIS 1-klik, sistem otomatis inject diamond dalam hitungan detik.", highlight: "inject diamond dalam hitungan detik" },
    { from: 270, to: 360, text: "100% legal, aman, anti-minus. Gas sekarang di kyustore.com.", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Springs Helper
  const sp = (delay: number, damping = 14, stiffness = 125) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Ambient micro-motion breathing float
  const floatY = Math.sin(frame / 22) * 5;

  // ==========================================
  // TIMELINE CHOREOGRAPHY (360 frames = 12s @ 30fps)
  // Phase 1 (Diagonal Showcase): Frame 0 - 180
  // Phase 2 (Instant QRIS & Injected Card): Frame 180 - 360
  // ==========================================

  // --- Corner HUD Anchors Entry ---
  const hudSpring = sp(0, 16, 120);
  const hudOpacity = interpolate(hudSpring, [0, 1], [0, 1]);
  const hudY = interpolate(hudSpring, [0, 1], [-20, 0]);

  // --- Phase 1: Diagonal Hero Staging Entry ---
  const heroAngleSpring = sp(12, 13, 110);
  const heroAngle = interpolate(heroAngleSpring, [0, 1], [8, -3]); // Dutch Angle settling at -3°
  const heroScale = interpolate(heroAngleSpring, [0, 1], [0.88, 1]);
  const heroOpacity = interpolate(heroAngleSpring, [0, 1], [0, 1]);

  // Specular Light Sweep across diagonal banner
  const sweepProgress = interpolate(
    frame,
    [45, 95],
    [-100, 200],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );

  // Package Ribbon Floating Animation
  const ribbonSpring = sp(35, 14, 130);
  const ribbonY = interpolate(ribbonSpring, [0, 1], [40, 0]);

  // Animated Cursor Simulation (Frame 95 - 135)
  const cursorProgress = interpolate(
    frame,
    [95, 125],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const cursorX = interpolate(cursorProgress, [0, 1], [720, 480]);
  const cursorY = interpolate(cursorProgress, [0, 1], [480, 290]);
  const isClicking = frame >= 126 && frame <= 136;
  const cursorScale = isClicking ? 0.85 : 1.0;
  const cursorOpacity = interpolate(frame, [95, 105], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Phase 1 Exit Transition (Frame 170 - 195)
  const p1Exit = interpolate(
    frame,
    [170, 192],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p1ExitY = interpolate(p1Exit, [0, 1], [0, -70]);
  const p1ExitOpacity = interpolate(p1Exit, [0, 0.7], [1, 0]);

  // --- Phase 2: Instant QRIS & Verified Injection (Frame 180 - 360) ---
  const p2Entry = interpolate(
    frame,
    [180, 205],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p2EntryY = interpolate(p2Entry, [0, 1], [70, 0]);
  const p2EntryScale = interpolate(p2Entry, [0, 1], [0.9, 1]);
  const p2EntryOpacity = interpolate(p2Entry, [0, 0.5], [0, 1]);

  // QRIS Laser Scanner Loop
  const scanLaserY = Math.sin(frame / 5) * 50;

  // Transaction Verified Card Spring (Frame 225)
  const successSpring = sp(225, 13, 150);
  const successY = interpolate(successSpring, [0, 1], [50, 0]);
  const successScale = interpolate(successSpring, [0, 1], [0.85, 1]);
  const successOpacity = interpolate(successSpring, [0, 1], [0, 1]);

  // CTA Pulse
  const ctaPulse = Math.sin(frame / 8) * 3;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Manrope', 'SF Pro Display', sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* 1. Background Halftone & Dot-Grid Pattern (Pinterest Aesthetic) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* 2. Soft Violet Ambient Glow in Upper & Center Staging */}
      <div
        style={{
          position: "absolute",
          top: "42%",
          left: "50%",
          width: 850,
          height: 850,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.12) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />

      {/* ========================================================================= */}
      {/* CORNER HUD ANCHORS (Pinterest Esports Standard)                           */}
      {/* ========================================================================= */}
      {/* Top-Left: Brand Logo */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 90,
          display: "flex",
          alignItems: "center",
          gap: 12,
          transform: `translateY(${hudY}px)`,
          opacity: hudOpacity,
          zIndex: 40,
        }}
      >
        <Img src={staticFile("kyu_logo.png")} style={{ height: 32, objectFit: "contain" }} />
        <div style={{ width: 1.5, height: 20, backgroundColor: "#CBD5E1" }} />
        <span style={{ fontSize: 13, fontWeight: 900, letterSpacing: "0.08em", color: textMuted }}>
          [ OFFICIAL ESPORT STORE ]
        </span>
      </div>

      {/* Top-Right: Live Server Indicator */}
      <div
        style={{
          position: "absolute",
          top: 80,
          right: 90,
          display: "flex",
          alignItems: "center",
          gap: 8,
          backgroundColor: bgCard,
          border: `1.5px solid ${emeraldSuccess}`,
          padding: "6px 16px",
          borderRadius: 20,
          boxShadow: "0 4px 15px rgba(16, 185, 129, 0.12)",
          transform: `translateY(${hudY}px)`,
          opacity: hudOpacity,
          zIndex: 40,
        }}
      >
        <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: emeraldSuccess, boxShadow: "0 0 8px #10B981" }} />
        <span style={{ fontSize: 12, fontWeight: 900, color: emeraldSuccess, letterSpacing: "0.05em" }}>
          AUTO INJECT 24/7 ONLINE
        </span>
      </div>

      {/* ========================================================================= */}
      {/* MASTER CONTENT CONTAINER: DEAD-CENTERED (top: 48%, left: 50%)             */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: "48%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ==================================================== */}
        {/* CHAPTER 1: DUTCH ANGLE HERO & WEEKLY PASS (0 - 180)  */}
        {/* ==================================================== */}
        {frame < 195 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p1ExitY + floatY}px)`,
              opacity: p1ExitOpacity,
            }}
          >
            {/* Header Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: bgCard,
                border: `2px solid ${kyuBorder}`,
                padding: "8px 22px",
                borderRadius: 24,
                marginBottom: 18,
                boxShadow: "0 4px 15px rgba(93, 63, 211, 0.08)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
              <span style={{ fontSize: 16, fontWeight: 900, color: kyuPurple, letterSpacing: "0.04em" }}>
                MOBILE LEGENDS SPECIAL PROMO
              </span>
            </div>

            {/* Extreme Scale Condensed Headline */}
            <h1
              style={{
                fontSize: 60,
                fontWeight: 900,
                lineHeight: 1.1,
                textAlign: "center",
                letterSpacing: "-0.04em",
                marginBottom: 30,
              }}
            >
              WEEKLY DIAMOND PASS, <br />
              <span style={{ color: kyuPurple }}>HEMAT HINGGA 70% SATSET!</span>
            </h1>

            {/* PINTEREST SIGNATURE: Diagonal Dutch Angle Hero Showcase (rotate -3deg) */}
            <div
              style={{
                width: 860,
                backgroundColor: bgCard,
                borderRadius: 36,
                border: isClicking ? `3px solid ${accentGold}` : `2.5px solid ${kyuPurple}`,
                padding: "32px 36px",
                boxShadow: "0 30px 80px rgba(93, 63, 211, 0.18), 0 10px 25px rgba(0,0,0,0.04)",
                transform: `rotate(${heroAngle}deg) scale(${heroScale})`,
                opacity: heroOpacity,
                position: "relative",
                overflow: "hidden",
                transition: "border-color 0.08s ease",
              }}
            >
              {/* Specular Light Sweep Stripe */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: 120,
                  left: `${sweepProgress}%`,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)",
                  transform: "skewX(-25deg)",
                  pointerEvents: "none",
                }}
              />

              {/* Card Header: Product & Micro-Tech Label */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <Img
                    src={staticFile("game_mlbb.jpg")}
                    style={{ width: 54, height: 54, borderRadius: 16, border: "2px solid #E2E8F0", objectFit: "cover" }}
                  />
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: textDark }}>Mobile Legends: Bang Bang</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: textMuted, letterSpacing: "0.06em" }}>
                      MOONTON OFFICIAL VERIFIED DISTRIBUTOR
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    backgroundColor: accentGold,
                    color: "#FFFFFF",
                    fontSize: 13,
                    fontWeight: 900,
                    padding: "6px 16px",
                    borderRadius: 14,
                    boxShadow: "0 4px 12px rgba(255, 159, 67, 0.35)",
                  }}
                >
                  ⚡ DISKON 70%
                </div>
              </div>

              {/* Main Product Feature Grid */}
              <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
                {/* Visual Color Pop Diamond Staging */}
                <div
                  style={{
                    width: 200,
                    height: 190,
                    borderRadius: 26,
                    backgroundColor: kyuLavender,
                    border: "2px solid #D8B4FE",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    boxShadow: "inset 0 2px 10px rgba(255, 255, 255, 0.8), 0 10px 25px rgba(93, 63, 211, 0.12)",
                  }}
                >
                  {/* Prismatic 3D Faceted Crystal Diamond (High-End Esport Render) */}
                  <svg width="86" height="86" viewBox="0 0 100 100" style={{ filter: "drop-shadow(0 10px 25px rgba(93, 63, 211, 0.45))" }}>
                    <defs>
                      <linearGradient id="facetTop" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="50%" stopColor="#E0F2FE" />
                        <stop offset="100%" stopColor="#BAE6FD" />
                      </linearGradient>
                      <linearGradient id="facetCyan" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#38BDF8" />
                        <stop offset="100%" stopColor="#0284C7" />
                      </linearGradient>
                      <linearGradient id="facetDeepBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#6366F1" />
                        <stop offset="100%" stopColor="#3730A3" />
                      </linearGradient>
                      <linearGradient id="facetPurple" x1="100%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#5B21B6" />
                      </linearGradient>
                      <linearGradient id="facetBottom" x1="50%" y1="0%" x2="50%" y2="100%">
                        <stop offset="0%" stopColor="#4338CA" />
                        <stop offset="100%" stopColor="#1E1B4B" />
                      </linearGradient>
                      <radialGradient id="sparkleGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
                        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                      </radialGradient>
                    </defs>

                    {/* Crown Upper Facets */}
                    <polygon points="26,16 74,16 88,38 12,38" fill="url(#facetTop)" stroke="#FFFFFF" strokeWidth="0.8" />
                    <polygon points="12,38 26,16 2,38" fill="url(#facetDeepBlue)" stroke="#A5B4FC" strokeWidth="0.6" />
                    <polygon points="74,16 88,38 98,38" fill="url(#facetPurple)" stroke="#A5B4FC" strokeWidth="0.6" />
                    <polygon points="26,16 50,38 74,16" fill="#F0F9FF" stroke="#FFFFFF" strokeWidth="0.8" />
                    <polygon points="12,38 50,38 26,16" fill="url(#facetCyan)" stroke="#E0F2FE" strokeWidth="0.6" />
                    <polygon points="74,16 50,38 88,38" fill="#818CF8" stroke="#E0F2FE" strokeWidth="0.6" />

                    {/* Lower Pavilion Facets Converging to Culet */}
                    <polygon points="2,38 12,38 50,92" fill="url(#facetDeepBlue)" stroke="#4338CA" strokeWidth="0.6" />
                    <polygon points="12,38 50,38 50,92" fill="url(#facetCyan)" stroke="#38BDF8" strokeWidth="0.8" />
                    <polygon points="50,38 88,38 50,92" fill="url(#facetPurple)" stroke="#6366F1" strokeWidth="0.8" />
                    <polygon points="88,38 98,38 50,92" fill="url(#facetBottom)" stroke="#31104B" strokeWidth="0.6" />

                    {/* Specular White Shimmer Lines */}
                    <line x1="26" y1="18" x2="48" y2="36" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" opacity="0.95" />
                    <circle cx="50" cy="38" r="5" fill="url(#sparkleGlow)" />
                    <circle cx="26" cy="18" r="7" fill="url(#sparkleGlow)" />
                    <circle cx="74" cy="18" r="4" fill="url(#sparkleGlow)" />
                  </svg>

                  <div style={{ fontSize: 13, fontWeight: 900, color: kyuPurple, marginTop: 8, letterSpacing: "0.04em" }}>
                    💎 220 DIAMONDS
                  </div>
                </div>

                {/* Pricing & Benefit Details */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                    <span style={{ fontSize: 44, fontWeight: 900, color: kyuPurple, letterSpacing: "-0.03em" }}>
                      Rp 27.500
                    </span>
                    <span style={{ fontSize: 20, color: textMuted, textDecoration: "line-through", fontWeight: 700 }}>
                      Rp 35.000
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: textDark }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Klaim 30 Diamond Tiap Hari Selama 7 Hari</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 800, color: textDark }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Cukup User ID & Zone ID (Tanpa Password)</span>
                    </div>
                  </div>

                  {/* Interactive Button */}
                  <div
                    style={{
                      marginTop: 18,
                      backgroundColor: isClicking ? kyuDark : kyuPurple,
                      color: "#FFFFFF",
                      padding: "14px 28px",
                      borderRadius: 18,
                      fontSize: 18,
                      fontWeight: 900,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 10px 25px rgba(93, 63, 211, 0.3)",
                      transform: isClicking ? "scale(0.96)" : "scale(1)",
                      transition: "transform 0.08s ease",
                    }}
                  >
                    <span>Pilih Weekly Pass</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Animated Mouse Pointer */}
              <div
                style={{
                  position: "absolute",
                  left: cursorX,
                  top: cursorY,
                  zIndex: 30,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.3))",
                  opacity: cursorOpacity,
                  transform: `scale(${cursorScale})`,
                }}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="#1E124A" stroke="#FFFFFF" strokeWidth="2">
                  <path d="M3 3l7 18 3-7 7-3L3 3z"/>
                </svg>
                <div
                  style={{
                    backgroundColor: isClicking ? accentGold : kyuPurple,
                    color: "white",
                    padding: "6px 14px",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 800,
                    marginLeft: -4,
                    marginTop: 22,
                  }}
                >
                  Klik Beli
                </div>
              </div>
            </div>

            {/* Bottom Floating Ribbon (Other Popular Games) */}
            <div
              style={{
                marginTop: 26,
                display: "flex",
                gap: 16,
                transform: `translateY(${ribbonY}px)`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: bgCard, border: "1.5px solid #E2E8F0", padding: "8px 18px", borderRadius: 16 }}>
                <Img src={staticFile("game_ff.png")} style={{ width: 22, height: 22, borderRadius: 6 }} />
                <span style={{ fontSize: 14, fontWeight: 800, color: textDark }}>Free Fire: 70 💎 Rp 9.500</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: bgCard, border: "1.5px solid #E2E8F0", padding: "8px 18px", borderRadius: 16 }}>
                <Img src={staticFile("game_valorant.jpg")} style={{ width: 22, height: 22, borderRadius: 6 }} />
                <span style={{ fontSize: 14, fontWeight: 800, color: textDark }}>Valorant: 475 VP Rp 52.000</span>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* CHAPTER 2: QRIS LASER SCAN & VERIFIED INJECTION     */}
        {/* ==================================================== */}
        {frame >= 175 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p2EntryY + floatY}px) scale(${p2EntryScale})`,
              opacity: p2EntryOpacity,
            }}
          >
            {/* SatSet Official Badge Header */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <Img src={staticFile("satset_proses.png")} style={{ height: 46, objectFit: "contain" }} />
            </div>

            <h1
              style={{
                fontSize: 54,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 26,
              }}
            >
              Bayar Scan QRIS Instan, <br />
              <span style={{ color: kyuPurple }}>Diamond Otomatis Masuk Akun!</span>
            </h1>

            {/* Split Screen Grid (QRIS Left vs Injection Verified Right) */}
            <div style={{ width: 860, display: "flex", gap: 22, alignItems: "center" }}>
              {/* Left Box: QRIS Interactive Scanner */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: bgCard,
                  borderRadius: 30,
                  border: `2px solid ${kyuBorder}`,
                  padding: "26px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow: "0 20px 45px rgba(93, 63, 211, 0.12)",
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 900, color: kyuDark, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  <span>QRIS SEMUA BANK & E-WALLET</span>
                </div>

                {/* QR Code Container with Glowing Laser */}
                <div
                  style={{
                    width: 210,
                    height: 210,
                    backgroundColor: "#111827",
                    borderRadius: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <svg width="130" height="130" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2">
                    <rect x="2" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="2" width="8" height="8" rx="1"></rect>
                    <rect x="14" y="14" width="8" height="8" rx="1"></rect>
                    <rect x="2" y="14" width="8" height="8" rx="1"></rect>
                    <line x1="6" y1="6" x2="6" y2="6"></line>
                    <line x1="18" y1="6" x2="18" y2="6"></line>
                    <line x1="18" y1="18" x2="18" y2="18"></line>
                    <line x1="6" y1="18" x2="6" y2="18"></line>
                  </svg>
                  {/* Glowing Laser Scan Beam */}
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      right: 10,
                      top: 105 + scanLaserY,
                      height: 4,
                      backgroundColor: "#00F0FF",
                      boxShadow: "0 0 14px #00F0FF, 0 0 24px #00F0FF",
                    }}
                  />
                </div>

                <div style={{ marginTop: 14, fontSize: 13, color: textMuted, fontWeight: 700 }}>
                  BCA • Mandiri • BRI • Dana • GoPay • OVO
                </div>
              </div>

              {/* Right Box: Verified Injection Card */}
              <div
                style={{
                  flex: 1.15,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  transform: `translateY(${successY}px) scale(${successScale})`,
                  opacity: successOpacity,
                }}
              >
                {/* Transaction Success Box */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 26,
                    border: `2px solid ${emeraldSuccess}`,
                    padding: "22px 26px",
                    boxShadow: "0 18px 40px rgba(16, 185, 129, 0.16)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 16, fontWeight: 900, color: emeraldSuccess, display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      TRANSAKSI BERHASIL
                    </span>
                    <span style={{ fontSize: 13, color: "#64748B", fontWeight: 800 }}>⚡ 0.8 DETIK</span>
                  </div>

                  <div style={{ fontSize: 22, fontWeight: 900, color: textDark, marginTop: 8 }}>
                    Weekly Diamond Pass Aktif
                  </div>
                  <div style={{ fontSize: 14, color: textMuted, marginTop: 4 }}>
                    User ID: <span style={{ fontWeight: 800, color: textDark }}>84920194 (2041)</span> • Otomatis masuk
                  </div>
                </div>

                {/* Trust Guarantees */}
                <div
                  style={{
                    backgroundColor: bgCard,
                    borderRadius: 24,
                    border: "1.5px solid #E2E8F0",
                    padding: "18px 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 12, backgroundColor: kyuLavender, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={kyuPurple} strokeWidth="2.5">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: textDark }}>Cukup User ID</div>
                      <div style={{ fontSize: 12, color: textMuted }}>Tanpa Password</div>
                    </div>
                  </div>

                  <div style={{ width: 1.5, height: 32, backgroundColor: "#E2E8F0" }} />

                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 12, backgroundColor: emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={emeraldSuccess} strokeWidth="2.5">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/>
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: textDark }}>100% Legal</div>
                      <div style={{ fontSize: 12, color: textMuted }}>Anti-Minus Akun</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Outro CTA Button */}
            <div
              style={{
                marginTop: 26,
                backgroundColor: kyuDark,
                color: "#FFFFFF",
                border: `2px solid ${accentGold}`,
                borderRadius: 36,
                padding: "20px 60px",
                fontSize: 26,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 22px 50px rgba(30, 18, 74, 0.35)",
                transform: `scale(${1 + ctaPulse * 0.01})`,
              }}
            >
              <span>Top Up Sekarang di kyustore.com</span>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={accentGold} strokeWidth="3">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* PINTEREST SIGNATURE FOOTER TICKER (Corner Anchoring)                      */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: 90,
          right: 90,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: bgCard,
          border: "1.5px solid #E2E8F0",
          borderRadius: 20,
          padding: "12px 28px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 35,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 800, color: textMuted }}>
          <span style={{ color: kyuPurple }}>METODE PEMBAYARAN:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 900, color: emeraldSuccess }}>
          ✓ VERIFIKASI RESMI
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: STRICTLY IN LOWER SAFE ZONE (bottom: 220px)            */}
      {/* ========================================================================= */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220, // Strict user rule: safe zone bottom 220px
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(30, 18, 74, 0.94)",
              backdropFilter: "blur(14px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 840,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 27, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: accentGold }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
